import { Conversation } from "./conversation";
import { CHAT_ICON, buildChatPane, uiStrings } from "./pane";
import { WidgetConfig } from "./remote_config";
import { ChatSession } from "./session";
import { hasSeenTeaser, markTeaserSeen } from "./storage";
import { injectStyles } from "./styles";
import {
    TEASER_RECHECK_MS,
    TeaserProbe,
    teaserCoversHostOverlay,
    teaserDelayMs,
    teaserText,
} from "./teaser";

/**
 * Bubble launcher + popover chat window. Pure DOM, no framework.
 *
 * Layout:
 *   - A 56px circular button fixed to bottom-right.
 *   - On click, expands into a 380x560 popover (full-screen on mobile).
 *   - Popover contains the chat window (pane.ts): header, scrollable
 *     message list, optional inline option chips (from the welcome
 *     message's `content_attributes.items`), and an input row.
 *
 * Talks to the backend through the page's Conversation (POST /message,
 * SSE for replies), which a window placed in the page shares.
 */
export interface LauncherMount {
    open: () => void;
    close: () => void;
    destroy: () => void;
}

export function mountLauncher(params: {
    config: WidgetConfig;
    conversation: Conversation;
    session: ChatSession;
    /** Base URL of the services API — passed through so the inline
     *  address-autocomplete dropdown can hit /v1/api/chat/places/*. */
    baseUrl: string;
    widgetToken: string;
    /** nl / fr / en — used as the `language` param on Places
     *  autocomplete calls so street name variants come back in the
     *  visitor's language. Detected at widget boot from
     *  document.documentElement.lang → navigator.language → agency
     *  default. */
    locale: string;
    /** Date.now() when AmaiChat.init() ran. The teaser delay counts from
     *  there, not from this mount, which waits on the config request. */
    initAt: number;
}): LauncherMount {
    const { config, conversation, session, baseUrl, widgetToken, locale, initAt } =
        params;
    const ui = uiStrings(locale);

    injectStyles(config.primary_color);

    const container = document.createElement("div");
    container.className = "amai-chat-root";
    document.body.appendChild(container);

    const launcher = document.createElement("button");
    launcher.className = "amai-chat-launcher";
    launcher.setAttribute("aria-label", ui.openChat);
    if (config.bubble_image_url) {
        // Image bubble: the uploaded artwork fills the round button.
        // The button keeps its primary-color background so a transparent
        // PNG/SVG sits on the agency's brand colour. If the image fails
        // to load (404, network), the onerror handler falls back to the
        // built-in SVG so the bubble never renders blank.
        const img = document.createElement("img");
        img.className = "amai-chat-launcher-img";
        img.src = config.bubble_image_url;
        img.alt = "";
        img.onerror = (): void => {
            img.remove();
            launcher.innerHTML = CHAT_ICON;
        };
        launcher.appendChild(img);
    } else {
        launcher.innerHTML = CHAT_ICON;
    }
    container.appendChild(launcher);

    const popover = document.createElement("div");
    popover.className = "amai-chat-popover";
    popover.setAttribute("aria-hidden", "true");
    container.appendChild(popover);

    const pane = buildChatPane({
        frame: popover,
        config,
        ui,
        locale,
        baseUrl,
        widgetToken,
        closeLabel: ui.closeChat,
        onClose: () => close(),
        onSend: (text, answering) => {
            void conversation.send(text, pane, answering);
        },
    });
    conversation.attach(pane);
    // Back in the open bubble after using a window in the page: an input
    // the next reply brings belongs here.
    popover.addEventListener("focusin", () => conversation.activate(pane));

    let isOpen = false;

    // Teaser bubble (chatbot.teaser_delay_seconds, off by default): a
    // short message next to the closed launcher, at most once per browser
    // session. Display only — clicking it goes through open() exactly
    // like the launcher, so no session or conversation exists until the
    // visitor actually opens the chat.
    const teaserMessage = teaserText(config);
    const teaserDelay = teaserMessage
        ? teaserDelayMs(config.teaser_delay_seconds, Date.now() - initAt)
        : undefined;

    // Never on top of something the site keeps on screen there (cookie
    // banner, modal, sticky bar): the bubble is mounted invisible, and
    // only shows while that spot is clear. It keeps looking once shown,
    // so a banner opened later ("Cookie-instellingen" in the footer)
    // hides it again until the visitor has answered.
    const teaserProbe: TeaserProbe<Element> = {
        elementsAt: (x, y) => document.elementsFromPoint(x, y),
        isOwn: (el) => container.contains(el),
        staysOnScreen: (el) => {
            if (el === document.body || el === document.documentElement) {
                return false;
            }
            const position = getComputedStyle(el).position;
            return position === "fixed" || position === "sticky";
        },
    };
    let teaserTimer: number | undefined;
    let teaserEl: HTMLDivElement | undefined;
    if (teaserDelay !== undefined && !hasSeenTeaser()) {
        teaserTimer = window.setTimeout(showTeaser, teaserDelay);
    }

    function showTeaser(): void {
        teaserTimer = undefined;
        if (isOpen) return;
        if (!teaserEl) teaserEl = buildTeaser();
        // A chat window in the page greets the visitor already: the
        // teaser waits until there is none (a single-page app may
        // navigate away from it).
        const blocked =
            conversation.hasOtherWindow(pane) ||
            teaserCoversHostOverlay(
                teaserEl.getBoundingClientRect(),
                teaserProbe,
            );
        const shown = teaserEl.classList.contains("amai-chat-teaser-visible");
        // Only a teaser the visitor actually got to see uses up the one
        // per session.
        if (!blocked && !shown) markTeaserSeen();
        teaserEl.classList.toggle("amai-chat-teaser-visible", !blocked);
        teaserTimer = window.setTimeout(showTeaser, TEASER_RECHECK_MS);
    }

    function buildTeaser(): HTMLDivElement {
        const bubble = document.createElement("div");
        bubble.className = "amai-chat-teaser";

        const body = document.createElement("button");
        body.type = "button";
        body.className = "amai-chat-teaser-body";
        // Operator-written text: plain text only, never renderRichText.
        const text = document.createElement("span");
        text.className = "amai-chat-teaser-text";
        text.textContent = teaserMessage;
        body.appendChild(text);
        body.addEventListener("click", () => open());
        bubble.appendChild(body);

        const dismiss = document.createElement("button");
        dismiss.type = "button";
        dismiss.className = "amai-chat-teaser-close";
        dismiss.setAttribute("aria-label", ui.dismissTeaser);
        dismiss.textContent = "×";
        dismiss.addEventListener("click", hideTeaser);
        bubble.appendChild(dismiss);

        container.appendChild(bubble);
        return bubble;
    }

    function hideTeaser(): void {
        if (teaserTimer !== undefined) {
            clearTimeout(teaserTimer);
            teaserTimer = undefined;
        }
        teaserEl?.remove();
        teaserEl = undefined;
    }

    function open(): void {
        if (isOpen) return;
        isOpen = true;
        // The visitor is in the chat: the teaser is done for this
        // session, whether it was showing or still pending.
        hideTeaser();
        if (teaserDelay !== undefined) markTeaserSeen();
        popover.classList.add("amai-chat-popover-open");
        popover.setAttribute("aria-hidden", "false");
        // A window in the page may have carried the chat on meanwhile.
        pane.revealed();
        conversation.activate(pane);
        void conversation.start();
        setTimeout(() => pane.inputField.focus(), 50);
    }

    function close(): void {
        if (!isOpen) return;
        isOpen = false;
        popover.classList.remove("amai-chat-popover-open");
        popover.setAttribute("aria-hidden", "true");
        conversation.leave(pane);
    }

    launcher.addEventListener("click", () => (isOpen ? close() : open()));

    function destroy(): void {
        hideTeaser();
        pane.closeGallery();
        conversation.detach(pane);
        session.disconnect();
        container.remove();
    }

    return { open, close, destroy };
}
